import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, User, Building2, ShieldCheck, Plus } from 'lucide-react';
import type { Cliente } from '@/lib/types';

interface Props {
  open: boolean;
  onClose: () => void;
  clientes: Cliente[];
  onSelect: (c: Cliente) => void;
}

const TIPO_ICON: Record<Cliente['tipo'], React.ReactNode> = {
  particular:  <User size={13} className="text-slate-500" />,
  empresa:     <Building2 size={13} className="text-blue-500" />,
  aseguradora: <ShieldCheck size={13} className="text-emerald-600" />,
};

export function ClienteSelector({ open, onClose, clientes, onSelect }: Props) {
  const [q, setQ] = useState('');

  const filtrados = clientes.filter(c => {
    if (!q) return true;
    const query = q.toLowerCase();
    return c.nombre.toLowerCase().includes(query) || c.nif.toLowerCase().includes(query) || c.email.toLowerCase().includes(query);
  });

  const handleSelect = (c: Cliente) => {
    onSelect(c);
    setQ('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={() => { setQ(''); onClose(); }}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="text-base">Seleccionar cliente</DialogTitle>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Buscar por nombre, NIF o email…"
              value={q}
              onChange={e => setQ(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1">
          {filtrados.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center px-4">
              <p className="text-sm text-muted-foreground">
                {q ? 'No se encontraron clientes' : 'No hay clientes en la base de datos'}
              </p>
            </div>
          ) : (
            filtrados.map(c => (
              <button
                key={c.id}
                onClick={() => handleSelect(c)}
                className="w-full flex items-start gap-3 px-5 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0 text-left"
              >
                <div className="mt-0.5 shrink-0">{TIPO_ICON[c.tipo]}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{c.nombre}</p>
                  <p className="text-xs text-muted-foreground">
                    {[c.nif, c.email, c.telefono].filter(Boolean).join(' · ')}
                  </p>
                  {(c.direccion || c.ciudad) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {[c.direccion, c.cp, c.ciudad].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
